import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../../../context/AppContext';
import useAppContent from '../../../hooks/useAppContent';
import useAppSettings from '../../../hooks/useAppSettings';
import useAnalytics from '../../../hooks/useAnalytics';
import { useBoardingPassData } from './hooks/useBoardingPassData';
import { useCanvasDrawing } from './hooks/useCanvasDrawing';
import { BoardingPassCard } from './components/BoardingPassCard';
import { useAssetLoader } from './hooks/useAssetLoader';
import {
  canvasToBlob,
  blobToDataUrl,
  uploadBoardingPass,
  loadShareableImage,
  createShareFile,
  canShareFiles,
  shareFile
} from './utils/boardingPassUpload';
import './BoardingPass.scss';

const BoardingPass = () => {
  const { actions } = useApp();
  const { appContent } = useAppContent();
  const { homepageData } = useAppSettings();
  const { boardingPassData, patchLoading } = useBoardingPassData();
  const { drawBoardingPassToCanvas } = useCanvasDrawing();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [shareFileReady, setShareFileReady] = useState(null);
  const { trackBoardingPassDownload } = useAnalytics();
  const fontsReadyRef = useRef(null);
  const passBlobRef = useRef(null);

  const waitForFonts = useCallback(() => {
    if (fontsReadyRef.current) return fontsReadyRef.current;

    const loadFonts = async () => {
      if (typeof document === 'undefined' || !document.fonts?.load) {
        return;
      }

      const descriptors = [
        '400 16px "TTFirsNeueNormal"',
        '500 16px "TTFirsNeueNormal"',
        '700 16px "TTFirsNeueMedium"',
        '400 16px "TTFirsNeueLight"'
      ];

      await Promise.allSettled(descriptors.map((d) => document.fonts.load(d)));
      await document.fonts.ready;
    };

    fontsReadyRef.current = loadFonts();
    return fontsReadyRef.current;
  }, []);


  const {
    getLocalImageSrc,
    loadThemedAssets,
    loadPassengerInfoIcons,
    loadPatchImage,
    loadImage
  } = useAssetLoader();

  useEffect(() => {
    if (boardingPassData && !patchLoading) {
      let isMounted = true;
      const timer = setTimeout(() => {
        waitForFonts().then(() => {
          if (isMounted) {
            setIsReady(true);
          }
        });
      }, 500);
      return () => {
        isMounted = false;
        clearTimeout(timer);
      };
    }
  }, [boardingPassData, patchLoading, waitForFonts]);

  const boardingPassDataRef = useRef(boardingPassData);
  boardingPassDataRef.current = boardingPassData;

  const homepageDataRef = useRef(homepageData);
  homepageDataRef.current = homepageData;

  const renderPassBlob = useCallback(async () => {
    if (passBlobRef.current) return passBlobRef.current;

    const build = (async () => {
      const boardingPassData = boardingPassDataRef.current;
      const homepageData = homepageDataRef.current;
      const preloadPromises = [waitForFonts()];
      const isSpace = boardingPassData.backgroundClass === 'space-theme';

      // Preload logos
      const logoUrls = [
        getLocalImageSrc(
          isSpace
            ? homepageData?.level_62_logo_simple_white?.url
            : homepageData?.level_62_logo_simple_black?.url,
          boardingPassData.backgroundClass
        ),
        getLocalImageSrc(
          isSpace
            ? homepageData?.spaceforce_icon_white?.url
            : homepageData?.spaceforce_icon_black?.url,
          boardingPassData.backgroundClass
        ),
        getLocalImageSrc(
          isSpace
            ? homepageData?.level_62_icon_white?.url
            : homepageData?.level_62_icon_black?.url,
          boardingPassData.backgroundClass
        )
      ].filter(Boolean);

      logoUrls.forEach((url) => {
        preloadPromises.push(loadImage(url).catch(() => null));
      });

      // Preload themed assets
      preloadPromises.push(
        loadThemedAssets(boardingPassData.backgroundClass).catch(() => null)
      );

      // Preload passenger info icons
      const iconSources = loadPassengerInfoIcons(
        boardingPassData.backgroundClass
      );
      Object.values(iconSources)
        .filter(Boolean)
        .forEach((iconUrl) => {
          preloadPromises.push(loadImage(iconUrl).catch(() => null));
        });

      // Preload patch
      if (boardingPassData.patchImage) {
        preloadPromises.push(
          loadPatchImage(
            boardingPassData.patchImage,
            boardingPassData.patchFile
          ).catch(() => null)
        );
      }

      await Promise.allSettled(preloadPromises);
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Draw to canvas
      const canvas = document.createElement('canvas');
      await drawBoardingPassToCanvas(canvas, boardingPassData);

      return canvasToBlob(canvas);
    })();

    passBlobRef.current = build;

    try {
      return await build;
    } catch (error) {
      passBlobRef.current = null;
      throw error;
    }
  }, [
    waitForFonts,
    getLocalImageSrc,
    loadImage,
    loadThemedAssets,
    loadPassengerInfoIcons,
    loadPatchImage,
    drawBoardingPassToCanvas
  ]);

  const handleDownload = async () => {
    trackBoardingPassDownload();

    if (!boardingPassData || !isReady) {
      return;
    }

    try {
      setIsDownloading(true);

      const blob = await renderPassBlob();

      const isMobile =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      if (isMobile) {
        const dataUrl = await blobToDataUrl(blob);
        if (!dataUrl) {
          throw new Error('Boarding pass could not be encoded for display');
        }

        let imageUrl = dataUrl;
        const uploadedUrl = await uploadBoardingPass(blob);
        if (uploadedUrl) {
          imageUrl = (await loadShareableImage(uploadedUrl)) || dataUrl;
        }

        const modal = document.createElement('div');
        modal.id = 'boarding-pass-modal';
        modal.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.95);
          z-index: 999999;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        `;

        modal.innerHTML = `
          <style>
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            #boarding-pass-modal img {
              max-width: 100%;
              max-height: 70vh;
              border-radius: 12px;
              box-shadow: 0 8px 32px rgba(0,0,0,0.5);
            }
            #boarding-pass-modal .instructions {
              color: white;
              text-align: center;
              margin-bottom: 20px;
              font-family: -apple-system, BlinkMacSystemFont, sans-serif;
              font-size: 16px;
              line-height: 1.6;
              max-width: 320px;
            }
          </style>
          <div class="instructions">
            Long press your boarding pass to save it.
          </div>
          <img src="${imageUrl}" alt="Boarding Pass" />
          <button class="btn-primary" style="margin-top: 20px" onclick="document.getElementById('boarding-pass-modal').remove()">
            Close
          </button>
        `;

        modal.addEventListener('click', (e) => {
          if (e.target === modal) {
            modal.remove();
          }
        });

        document.body.appendChild(modal);
        actions.setBoardingPassDownloaded(true);
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `level-62-boarding-pass-${boardingPassData.patchId || Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        actions.setBoardingPassDownloaded(true);
      }
    } catch (error) {
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const patchId = boardingPassData?.patchId;

  useEffect(() => {
    if (!patchId || !isReady) return;

    let isMounted = true;

    renderPassBlob()
      .then((blob) => {
        if (!isMounted) return;
        const file = createShareFile(blob, patchId);
        setShareFileReady(file && canShareFiles(file) ? file : null);
      })
      .catch(() => {
        if (isMounted) setShareFileReady(null);
      });

    return () => {
      isMounted = false;
    };
  }, [patchId, isReady, renderPassBlob]);

  const handleShare = () => {
    if (shareFileReady) shareFile(shareFileReady);
  };

  if (!boardingPassData || patchLoading || !isReady) {
    return (
      <div className="slide BoardingPass">
        <div
          className="BoardingPass__container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px'
          }}
        >
          {/* Loading state - consider adding LoadingSpinner here if needed */}
        </div>
      </div>
    );
  }

  return (
    <div className="slide BoardingPass">
      <div className="BoardingPass__container">
        <p className="BoardingPass__title">
          {appContent?.acf?.slide_6_boarding_pass_title}
        </p>

        <BoardingPassCard
          boardingPassData={boardingPassData}
          homepageData={homepageData}
          appContent={appContent}
        />

        <div className="BoardingPass__ready-text">
          <p>Download your Boarding Pass and share to socials!</p>

          <div className="BoardingPass__actions">
            <button
              className="btn btn-primary inverted"
              onClick={handleDownload}
              disabled={isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download'}
            </button>
            {shareFileReady && (
              <button className="btn btn-primary" onClick={handleShare}>
                Share
              </button>
            )}
          </div>

          <p className="caption">
            You will lose this boarding pass if you exit this experience.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BoardingPass;
